/**
 * Date and Time Formatting Utilities
 * Standardizes ISO strings, Traditional Chinese calendar displays, and chronological group sorting.
 */

import { BloodPressureRecord, DailyRecordGroup, DateRangePreset } from '@/types/record';
import { calculateDailySummary } from './calculations';

/**
 * Get current local date formatted as YYYY-MM-DD
 */
export function getTodayDateString(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get current local time formatted as HH:mm
 */
export function getCurrentTimeString(): string {
  const d = new Date();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Combine separate date string (YYYY-MM-DD) and time string (HH:mm) into an ISO-like or local timestamp
 */
export function combineDateAndTime(dateStr: string, timeStr: string): string {
  const cleanDate = dateStr.trim();
  const cleanTime = timeStr.trim();
  // If time only has HH:mm, append :00
  const normalizedTime = cleanTime.length === 5 ? `${cleanTime}:00` : cleanTime;
  return `${cleanDate}T${normalizedTime}`;
}

/**
 * Format date string or Date object into specified format
 */
export function formatDate(
  dateInput: string | Date,
  format: 'YYYY-MM-DD' | 'YYYY/MM/DD' | 'zh' | 'fullZh' = 'YYYY-MM-DD'
): string {
  if (!dateInput) return '';

  let d: Date;
  if (typeof dateInput === 'string') {
    // If input is YYYY-MM-DD, parse year, month, day directly to avoid timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      const [year, month, day] = dateInput.split('-').map(Number);
      d = new Date(year, month - 1, day);
    } else {
      d = new Date(dateInput);
    }
  } else {
    d = dateInput;
  }

  if (isNaN(d.getTime())) return String(dateInput);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  switch (format) {
    case 'YYYY/MM/DD':
      return `${year}/${month}/${day}`;
    case 'zh':
      return `${year}年${month}月${day}日`;
    case 'fullZh': {
      const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
      const weekday = weekdays[d.getDay()];
      return `${year}年${month}月${day}日 星期${weekday}`;
    }
    case 'YYYY-MM-DD':
    default:
      return `${year}-${month}-${day}`;
  }
}

/**
 * Format time string (HH:mm:ss or ISO string) to HH:mm
 */
export function formatTime(timeInput: string | Date): string {
  if (!timeInput) return '';

  if (typeof timeInput === 'string') {
    // If it's already HH:mm or HH:mm:ss
    if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeInput)) {
      return timeInput.slice(0, 5);
    }
    // If it's an ISO string (e.g. 2026-08-28T08:30:00Z)
    const match = timeInput.match(/T(\d{2}:\d{2})/);
    if (match) {
      return match[1];
    }
    const d = new Date(timeInput);
    if (!isNaN(d.getTime())) {
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return timeInput;
  }

  const hours = String(timeInput.getHours()).padStart(2, '0');
  const minutes = String(timeInput.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Format full date & time (e.g. 2026-08-28 08:30)
 */
export function formatDateTime(dateTimeInput: string | Date): string {
  if (!dateTimeInput) return '';
  const datePart = formatDate(dateTimeInput, 'YYYY-MM-DD');
  const timePart = formatTime(dateTimeInput);
  return `${datePart} ${timePart}`;
}

/**
 * Get Traditional Chinese weekday name from date string
 */
export function getWeekdayZh(dateStr: string): string {
  if (!dateStr) return '';
  let d: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d.getTime())) return '';
  const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
  return `星期${weekdays[d.getDay()]}`;
}

/**
 * Get Traditional Chinese day period label (晨間 / 午後 / 晚間 / 深夜)
 */
export function getDayPeriodZh(timeStr: string): string {
  if (!timeStr) return '';
  const cleanTime = timeStr.slice(0, 5);
  const hour = parseInt(cleanTime.split(':')[0], 10);
  if (isNaN(hour)) return '';

  if (hour >= 5 && hour < 12) return '晨間';
  if (hour >= 12 && hour < 18) return '午後';
  if (hour >= 18 && hour < 23) return '晚間';
  return '深夜';
}

/**
 * Get relative time description in Traditional Chinese
 */
export function getRelativeTimeString(dateInput: string | Date): string {
  if (!dateInput) return '';

  let targetDate: Date;
  if (typeof dateInput === 'string') {
    targetDate = new Date(dateInput);
  } else {
    targetDate = dateInput;
  }

  if (isNaN(targetDate.getTime())) return String(dateInput);

  const now = new Date();
  const diffMs = now.getTime() - targetDate.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60 && diffSeconds >= 0) {
    return '剛剛';
  }
  if (diffMinutes < 60 && diffMinutes > 0) {
    return `${diffMinutes} 分鐘前`;
  }
  if (diffHours < 24 && diffHours > 0) {
    return `${diffHours} 小時前`;
  }
  if (diffDays === 1) {
    return `昨天 ${formatTime(targetDate)}`;
  }
  if (diffDays === 2) {
    return `前天 ${formatTime(targetDate)}`;
  }
  if (diffDays < 7 && diffDays > 0) {
    return `${diffDays} 天前`;
  }

  return formatDate(targetDate, 'YYYY/MM/DD');
}

/**
 * Group flat records by calendar day (measured_date)
 * Returns daily groups sorted descending by date (newest date first).
 * Records within each day are sorted chronologically ascending by measured_time (or descending if specified).
 */
export function groupRecordsByDate(
  records: BloodPressureRecord[],
  recordSortOrder: 'asc' | 'desc' = 'asc'
): DailyRecordGroup[] {
  if (!records || records.length === 0) return [];

  const map = new Map<string, BloodPressureRecord[]>();

  for (const record of records) {
    const date = record.measured_date || (record.measured_at ? record.measured_at.slice(0, 10) : getTodayDateString());
    if (!map.has(date)) {
      map.set(date, []);
    }
    map.get(date)!.push(record);
  }

  // Get distinct dates sorted descending (latest date first)
  const sortedDates = Array.from(map.keys()).sort((a, b) => b.localeCompare(a));

  const groups: DailyRecordGroup[] = [];

  for (const date of sortedDates) {
    const dayRecords = map.get(date) || [];

    // Sort records within the day by measured_time
    dayRecords.sort((a, b) => {
      const timeA = a.measured_time || (a.measured_at ? a.measured_at.slice(11, 16) : '00:00');
      const timeB = b.measured_time || (b.measured_at ? b.measured_at.slice(11, 16) : '00:00');
      if (recordSortOrder === 'asc') {
        return timeA.localeCompare(timeB);
      }
      return timeB.localeCompare(timeA);
    });

    const summary = calculateDailySummary(dayRecords, date);
    const formattedDateZh = formatDate(date, 'fullZh');

    groups.push({
      date,
      formattedDateZh,
      records: dayRecords,
      summary,
    });
  }

  return groups;
}

/**
 * Calculate start and end date based on preset
 */
export function getDateRangeFromPreset(preset: DateRangePreset): {
  startDate?: string;
  endDate?: string;
} {
  const today = new Date();
  const endDate = getTodayDateString();

  if (preset === 'all') {
    return { startDate: undefined, endDate: undefined };
  }

  if (preset === 'custom') {
    return { startDate: undefined, endDate: undefined };
  }

  let days = 7;
  switch (preset) {
    case '7d':
      days = 7;
      break;
    case '14d':
      days = 14;
      break;
    case '30d':
      days = 30;
      break;
    case '90d':
      days = 90;
      break;
  }

  const startD = new Date(today);
  startD.setDate(today.getDate() - (days - 1));
  const startDate = formatDate(startD, 'YYYY-MM-DD');

  return { startDate, endDate };
}
